import { defineStore } from 'pinia';
import { errorsParser } from '~/utils/ss/errorsParser';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreResponsibleGamingPage } from '../strapi/responsibleGamingPage';
import { softswiss } from '../transport/softswiss';
import type {
  ISsLimit,
  ISsLimitCreate,
  tLimitByPeriod,
  tLimitByStatus,
  tSsLimitPeriod,
  tSsLimitPeriodShort,
  tSsLimitTypeShort,
} from '~/types/softswiss/ssLimits';

function formatLimitByPeriod(
  allLimits: ISsLimit[],
  typeForFilter: tSsLimitTypeShort
): tLimitByPeriod {
  const periods: Array<tSsLimitPeriodShort> = ['day', 'week', 'month'];
  const filteredLimits = allLimits.filter((l) => l.type === typeForFilter);

  const activeFilteredLimits = filteredLimits.filter((l) => l.status === 'active');
  const deletePendingFilteredLimits = filteredLimits.filter(
    (l) => l.status === 'unconfirmed_disable'
  );
  const limitsByPeriod = periods.reduce((acc, period) => {
    const limitsForPeriod = filteredLimits.filter((l) => l.period === period);
    const active = limitsForPeriod.find((l) => l.status === 'active') || null;
    const unconfirmed = limitsForPeriod.find((l) => l.status === 'unconfirmed') || null;
    const unconfirmed_disable =
      limitsForPeriod.find((l) => l.status === 'unconfirmed_disable') || null;
    acc[period] = {
      active: active || unconfirmed_disable || null,
      unconfirmed,
      unconfirmed_disable,
    };

    return acc;
  }, {} as Record<tSsLimitPeriodShort, tLimitByStatus>);

  return {
    limits: limitsByPeriod,
    count: activeFilteredLimits.length || deletePendingFilteredLimits.length || 0,
  };
}

export const useStoreSsLimits = defineStore('ssUserLimits', {
  state: () => ({
    limits: [] as ISsLimit[],
    isLoadedOnce: false,
    limitsOptions: {
      deposit: ['day', 'week', 'month'],
      loss: ['day', 'week', 'month'],
      wager: ['day', 'week', 'month'],
      coolingoff: ['day', 'day_3', 'week', 'month', 'month_3', 'month_6'],
      selfexclusion: ['day', 'day_3', 'week', 'month', 'month_3', 'month_6'],
      reality_check: [15, 30, 45, 60],
      activity: ['day', 'week', 'month', 'month_12'],
    },
  }),
  getters: {
    exclusionAndCoolingOffOptions(): Record<tSsLimitPeriod, string> {
      const storeRespGamingPage = useStoreResponsibleGamingPage();
      return {
        day: storeRespGamingPage.periodOptions.day,
        day_3: storeRespGamingPage.periodOptions.day_3,
        week: storeRespGamingPage.periodOptions.week,
        month: storeRespGamingPage.periodOptions.month,
        month_3: storeRespGamingPage.periodOptions.month_3,
        month_6: storeRespGamingPage.periodOptions.month_6,
      };
    },
    isAnyDepositLimitExists(): boolean {
      const depositLimits = this.limits.filter((l) => l.type === 'deposit');
      if (!depositLimits.length) {
        return false;
      }
      const storeCurrencyMenu = useStoreCurrencyMenu();
      for (let i = 0; i < depositLimits.length; i++) {
        const limit = depositLimits[i];
        const isSomeActiveLimit = limit.accounts.some(
          (a) =>
            !storeCurrencyMenu.isCryptoCurency(a.currency) &&
            a.amount_cents !== Number(MAX_LIMIT_SS)
        );
        if (isSomeActiveLimit) {
          return true;
        }
      }
      return false;
    },
    coolingOffLimitActive(): ISsLimit | null {
      return (
        this.limits.find((limit) => limit.type === 'coolingoff' && limit.status === 'active') ||
        null
      );
    },
    coolingOffLimitUnconfirmed(): ISsLimit | null {
      return (
        this.limits.find(
          (limit) => limit.type === 'coolingoff' && limit.status === 'unconfirmed'
        ) || null
      );
    },
    coolingOffLimitUnconfirmedDisable(): ISsLimit | null {
      return (
        this.limits.find(
          (limit) => limit.type === 'coolingoff' && limit.status === 'unconfirmed_disable'
        ) || null
      );
    },
    coolingOffLimitPeriod(): string | null {
      return (
        this.coolingOffLimitActive?.period || this.coolingOffLimitUnconfirmedDisable?.period || null
      );
    },
    depositLimitsByPeriod(): tLimitByPeriod {
      return formatLimitByPeriod(this.limits, 'deposit');
    },
    lossLimitsByPeriod(): tLimitByPeriod {
      return formatLimitByPeriod(this.limits, 'loss');
    },
    wagerLimitsByPeriod(): tLimitByPeriod {
      return formatLimitByPeriod(this.limits, 'wager');
    },
    sessionLimitData(): {
      active: ISsLimit | null;
      unconfirmed: ISsLimit | null;
      unconfirmed_disable: ISsLimit | null;
      period: number | null;
    } {
      const active =
        this.limits.find((limit) => limit.type === 'session' && limit.status === 'active') || null;
      const unconfirmed =
        this.limits.find((limit) => limit.type === 'session' && limit.status === 'unconfirmed') ||
        null;
      const unconfirmed_disable =
        this.limits.find(
          (limit) => limit.type === 'session' && limit.status === 'unconfirmed_disable'
        ) || null;

      return {
        active,
        unconfirmed,
        unconfirmed_disable,
        period: Number(active?.period) || Number(unconfirmed_disable?.period) || null,
      };
    },
  },
  actions: {
    // async getAvailableLimitsOptions() {
    //   const data = await softswiss().get<ISsLimit[]>({
    //     path: '/api/info/user_limits_options',
    //     cacheble: false,
    //   });
    //   console.log(data);
    //   this.limitsOptions = data || [];
    // },
    async getUserCurrentLimits(force = false) {
      const data = await softswiss().get<ISsLimit[]>({
        path: '/api/user_limits',
        cacheble: false,
        force,
      });
      this.limits = data || [];
      this.isLoadedOnce = true;
    },
    async removeUserLimitById(id: string | number) {
      const response = await softswiss().delete({
        path: `/api/user_limits/${id}`,
        cacheble: false,
        getErrors: true,
      });
      if (response.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          success: false,
          errors: errorsKeys,
        };
      }
      await this.getUserCurrentLimits(true);
      return {
        success: true,
      };
    },
    async setUserLimit(data: ISsLimitCreate) {
      const response = await softswiss().post({
        path: '/api/user_limits',
        payload: { user_limit: data },
        getErrors: true,
      });
      if (response.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          success: false,
          errors: errorsKeys,
        };
      }
      await this.getUserCurrentLimits(true);
      return {
        success: true,
      };
    },
    clearStore() {
      this.$reset();
    },
  },
});
